/* ===========================================================================
   "Which World Marathon Major Are You?" — quiz engine
   sportplus.sg x TCS Sydney Marathon, ballot window 29 Sep – 19 Oct 2026.

   ── THIS FILE EXISTS TWICE, AND THE TWO COPIES MUST STAY IDENTICAL ──────────
   The campaign runs on BOTH storefronts, which are separate Shopify stores with
   separate themes, so each needs its own copy of this asset:

     sportplus-shopify-theme/assets/sp-wmm-quiz.js
     the-hub-shopify-theme/assets/hub-wmm-quiz.js

   They are byte-identical on purpose and `_verify/wmm-quiz-parity-test.mjs`
   FAILS THE BUILD if they drift. Edit one, copy it to the other, or the two
   sites show different questions halfway through a live campaign — which is the
   kind of thing nobody notices until a reader screenshots both.

   Nothing in here is theme-specific: the host section supplies the markup, the
   styling and the config (window.WMM_QUIZ), and the engine only ever looks for
   `[data-wmm-quiz]`. Keep it that way. Anything that needs to differ between
   the two sites belongs in the section, not here.

   No config = no network calls, so an unconfigured theme preview is inert
   rather than half-wired.

   WHAT THE EMAIL IS ASKED IN EXCHANGE FOR is a setting (CFG.captureMode — see MODE
   in the membership block). The default since 2026-09-29 is the TEASER: the Major,
   its photo, medal and the ballot show at once, and the email unlocks the full
   report (persona, summary, match bars, course profile, share card). 'gate' puts the
   email in front of every result (the launch version, same day); 'optional' puts
   nothing behind it. ATHLO+ retired its own full pre-result gate in 2026-09 because
   guests hit a wall before any payoff — the teaser is the middle ground: the payoff
   first, the email for the rest.
   =========================================================================== */
(function () {
  'use strict';

  /* ONE QUIZ PER PAGE, BOUND ONCE (2026-09-30: the section can now be added to any page).
     Every copy of the section prints its own <script src> for this file, so on a page with
     two copies it would EXECUTE twice and bind the same markup twice (double clicks, double
     tracking rows). The flag makes the second run a no-op. The config follows the same rule:
     each section writes `window.WMM_QUIZ = window.WMM_QUIZ || {…}`, so the FIRST live copy's
     settings win — and that is the copy bound below. Any further copy is hidden rather than
     left on screen as a quiz whose buttons do nothing. */
  if (window.__wmmQuizBound) return;
  var CFG = window.WMM_QUIZ || {};
  var roots = document.querySelectorAll('[data-wmm-quiz]');
  var root = roots[0];
  if (!root) return;
  window.__wmmQuizBound = true;
  for (var ri = 1; ri < roots.length; ri++) { roots[ri].hidden = true; roots[ri].setAttribute('data-wmm-duplicate', ''); }

  /* ---------------------------------------------------------------------
     THE EIGHT RESULTS. Personas and hooks are TCS Sydney Marathon's, from
     their campaign brief, with one exception noted on sydney.hook below.
     `tone` seeds the card artwork only — it is not brand colour for the page,
     which wears whichever host site's own tokens.

     `race` is each event's own official title, matching the logo the client
     supplied (sponsor names included — "BMW Berlin Marathon", not "Berlin").
     `fx`/`fy` are the focal point of that Major's hero photo, as fractions, so
     the banner and the card crop to the landmark instead of the geometric
     centre. `logoBg` is the ground colour the supplied logo sits on, so its
     tile reads as the logo's own background rather than a box around it.
     Photos, medals, logos and elevation strips are supplied by the host section
     as URLs (CFG.assets) — the engine never knows where they live.
     --------------------------------------------------------------------- */
  var MAJORS = {
    sydney: {
      city: 'Sydney', persona: 'The Trailblazer', featured: true, tone: ['#1ba8c4', '#0d5f7a'],
      race: 'TCS Sydney Marathon', fx: .47, fy: .5, logoBg: '#ffffff',
      /* CHANGED FROM THE BRIEF, awaiting client sign-off. Their line was "the
         closest Major to Southeast Asia", which is not true of this site's
         audience: Singapore→Tokyo is 5,314km against Singapore→Sydney 6,306km,
         and Tokyo is a card in this same quiz, so a reader can hold the two up
         side by side. Bangkok and Manila are further skewed still; only from
         Jakarta is Sydney nearest. Replaced with claims that are true and do
         the same job. Revert this string if the client prefers their original.

         SECOND CORRECTION, 2026-09-29 — and this one was ours. The rewrite said
         "the newest Major on the list", which stopped being true on 10 Jun 2026
         when Cape Town was confirmed as the eighth Major: Cape Town is on this
         same list, wearing a NEW badge, so the two claims contradicted each other
         on one screen. Sydney (2025) and Cape Town are the two newest.

         THIRD CORRECTION, 2026-09-29 (live fact-check): "the only one here taking entries
         today" was false inside the campaign window — Berlin's 2027 lottery opens 1 Oct and
         Chicago's 2027 application opens 8 Oct. And this line is DRAWN ONTO SHARED CARDS,
         which outlive the ballot, so it now carries no time-sensitive claim at all. The
         ballot panel says what is open and when, and switches itself off at the close. */
      hook: 'Harbour, bridge and one of the two newest Majors. No qualifying time, and a free ballot to get in.'
    },
    tokyo:    { city: 'Tokyo',    persona: 'The Precisionist',  tone: ['#d94f6a', '#6d1f36'],
                race: 'Tokyo Marathon', fx: .45, fy: .45, logoBg: '#ffffff',
                hook: 'Discipline and detail. Nothing left to chance, right down to the shoelaces.' },
    boston:   { city: 'Boston',   persona: 'The Purist',        tone: ['#3f8f5f', '#16402a'],
                race: 'Boston Marathon', fx: .47, fy: .45, logoBg: '#111e49',
                hook: 'You earned your place. That is the whole point, and everyone on that start line knows it.' },
    london:   { city: 'London',   persona: 'The Crowd-Rider',   tone: ['#c33f4f', '#5c1723'],
                race: 'TCS London Marathon', fx: .5, fy: .3, logoBg: '#ffffff',
                hook: 'You feed off the roar. Twenty-six miles of noise and you would still want more.' },
    berlin:   { city: 'Berlin',   persona: 'The Record-Chaser', tone: ['#d6a83a', '#5e4410'],
                race: 'BMW Berlin Marathon', fx: .4, fy: .28, logoBg: '#ffffff',
                hook: 'Flat, fast, PB energy. You did not come here for the sightseeing.' },
    chicago:  { city: 'Chicago',  persona: 'The Pragmatist',    tone: ['#3f7fc4', '#173c66'],
                race: 'Bank of America Chicago Marathon', fx: .44, fy: .42, logoBg: '#ffffff',
                hook: 'Big city, no fuss. Turn up, run well, go home happy.' },
    /* "New York City", not "New York" — client's correction, 2026-09-29. The race is the
       TCS New York City Marathon; "New York" alone reads as the state. The KEY stays
       `newyork` on purpose: it is in every shared link (?wmm=newyork). Note the SHOPIFY
       TAG does change — the bookings function slugs the display name, so it is now
       `wmm-new-york-city`. Changed before launch, so no customer carries the old one. */
    newyork:  { city: 'New York City', persona: 'The Showstopper', tone: ['#8a5fd0', '#2f1d59'],
                race: 'TCS New York City Marathon', fx: .52, fy: .45, logoBg: '#ffffff',
                hook: 'Five boroughs, all eyes on you. If it is not a spectacle, why bother?' },
    capetown: { city: 'Cape Town', persona: 'The Free Spirit',  isNew: true, tone: ['#e0863f', '#5e2f12'],
                race: 'Sanlam Cape Town Marathon', fx: .47, fy: .36, logoBg: '#020028',
                hook: 'Table Mountain, sea views, Africa’s first Major. You run for the soul and the story, not the clock.' }
  };

  /* EDITABLE WORDING (owner's boss, 2026-09-30): every fixed line on the result screen can be
     reworded in the theme editor. CFG.copy carries the section's text settings; each call site
     keeps the exact pre-edit sentence as its fallback, so a blank field reads as it always did.
     {tokens} are filled per call; an unknown one stays visible so a typo shows up in QA.
     Persona NAMES and one-liners are DISPLAY-ONLY overrides (personaLabel / hookLabel): the
     canonical m.persona / m.city still go to the client's response sheet and the backend, and
     restoreFromCard() still matches on them — so a reworded persona never splits TCS's data
     or breaks an old card link. Questions and answers are deliberately NOT editable: saved
     results are restored by matching the exact answer wording. */
  var COPY = CFG.copy || {};
  function fillCopy(tpl, map) {
    return String(tpl).replace(/\{(\w+)\}/g, function (all, k) {
      return Object.prototype.hasOwnProperty.call(map, k) ? map[k] : all;
    });
  }
  function copy(key, fallback, map) {
    return fillCopy(COPY[key] || fallback, map || {});
  }
  (function () {
    var P2 = CFG.personas || {};
    Object.keys(MAJORS).forEach(function (k) {
      var o = P2[k] || [];
      MAJORS[k].personaLabel = o[0] || MAJORS[k].persona;
      MAJORS[k].hookLabel = o[1] || MAJORS[k].hook;
    });
  })();

  /* Tie-break, Sydney first. This is the ONE place the featured result gets a
     thumb on the scale, and it is defensible because of when it fires: only
     when two Majors finish level, meaning the answers genuinely did not
     separate them, so either is equally true of that athlete.

     THE DISTRIBUTION, measured over ALL 16,384 answer paths (4^7):
       Sydney 16.6 · New York City 13.6 · Chicago 13.5 · Cape Town 12.6
       Berlin 11.6 · Boston 11.3 · London 10.6 · Tokyo 10.3        (even = 12.5)
     Sydney in the top three on 48.3% of paths; 0.39 Majors on a flat 0% per run.
     _verify/wmm-quiz-parity-test.mjs re-enumerates this on every commit and
     fails if Sydney leaves 15.5–17.5% or any Major drops below 9.5%.
     Inflating Sydney further was considered and rejected: the share loop IS the
     distribution here, and a result that does not feel like you is a result
     nobody posts. */
  var TIEBREAK = ['sydney', 'tokyo', 'boston', 'london', 'berlin', 'chicago', 'newyork', 'capetown'];

  /* Seven questions: the brief's six topics, plus the ASICS shoe question the
     client added 2026-09-29. `w` maps an option to the Majors it points at.
     Tuned against a full enumeration — see the distribution note above before
     editing any number here, because changing one weight moves every result. */
  var QUESTIONS = [
    { id: 'vibe', topic: 'Race-day vibe', q: '5am on race morning. What’s the mood?', opts: [
      { label: 'Kit laid out since Thursday. The alarm was a formality.', w: { tokyo: 3, berlin: 1, chicago: 1 } },
      { label: 'Nervous energy, but I earned this one.',            w: { boston: 3, london: 1, newyork: 1 } },
      { label: 'Buzzing. Get me to that start line.',               w: { london: 4, newyork: 2 } },
      { label: 'Weirdly calm. It’s just a run.',               w: { chicago: 3, capetown: 2, sydney: 1 } }
    ]},
    { id: 'pull', topic: 'What pulls you to a start line', q: 'Be honest: why do you actually sign up?', opts: [
      { label: 'The clock. There’s a number I want.',          w: { berlin: 3, tokyo: 2, boston: 1 } },
      { label: 'The crowd. Nothing else sounds like it.',           w: { london: 3, newyork: 2, chicago: 1 } },
      { label: 'The place. I want to see the city on foot.',        w: { sydney: 3, capetown: 1, newyork: 1 } },
      { label: 'The story. I want something to tell afterwards.',   w: { capetown: 3, sydney: 2, boston: 1 } }
    ]},
    { id: 'training', topic: 'Training style', q: 'Your training plan is…', opts: [
      { label: 'A spreadsheet. Colour-coded.',                      w: { tokyo: 3, berlin: 2 } },
      { label: 'Whatever the coach sends me.',                      w: { boston: 2, chicago: 2, london: 1 } },
      { label: 'Consistent. Boring on purpose.',                    w: { chicago: 3, berlin: 2, tokyo: 1 } },
      { label: 'Vibes, and a long run on Sunday.',                  w: { capetown: 3, sydney: 2 } }
    ]},
    { id: 'reward', topic: 'Post-race reward', q: 'You’ve crossed the line. First thing you want?', opts: [
      { label: 'The medal photo. Properly composed.',               w: { newyork: 4, tokyo: 2, london: 1 } },
      { label: 'A drink with whoever’s still standing.',       w: { london: 4, chicago: 2, capetown: 1 } },
      { label: 'Ocean. Immediately.',                              w: { sydney: 3, capetown: 2 } },
      { label: 'The splits. Show me the splits.',                   w: { berlin: 3, boston: 2, tokyo: 1 } }
    ]},
    { id: 'goal', topic: 'Your next marathon goal', q: 'Your next marathon is about…', opts: [
      { label: 'A PB. No other reason.',                            w: { berlin: 4, tokyo: 1 } },
      { label: 'Finally getting the qualifier.',                    w: { boston: 3, berlin: 1 } },
      { label: 'Collecting another star.',                                w: { tokyo: 2, newyork: 2, sydney: 1, capetown: 1 } },
      { label: 'Just finishing it happy.',                          w: { capetown: 3, chicago: 2, sydney: 1 } }
    ]},
    { id: 'travel', topic: 'How far you’d travel to race', q: 'How far would you fly for a start line?', opts: [
      { label: 'Long haul. All of them, eventually.',               w: { boston: 2, newyork: 3, london: 1 } },
      { label: 'Eight hours is fine for a long weekend.',     w: { sydney: 3, chicago: 1 } },
      { label: 'Somewhere I’d holiday anyway.',          w: { capetown: 3, sydney: 2 } },
      { label: 'Big city, direct flight, in and out.',        w: { chicago: 3, tokyo: 2, berlin: 1 } }
    ]},
    /* THE ASICS QUESTION (client copy, verbatim). Last on purpose: it hands straight
       into the "presented by ASICS" loading beat.
       The weights are grounded in the COURSES, using the elevation profiles the client
       supplied: "light, fast and aggressive" points at the three flattest Majors
       (Tokyo 60m, Berlin 73m, Chicago 74m of climbing); "protective and reliable" at
       the hilliest (Sydney 317m, New York City and Boston ~246m, Cape Town). The other
       two follow the personas. Within those limits the exact numbers were chosen by
       searching every candidate against the full enumeration — this one question took
       the rarest result from 9.2% to 10.3% without moving Sydney off its share. */
    { id: 'shoe', topic: 'Race-day shoe', q: 'What’s your ideal race-day shoe?', opts: [
      { label: 'Maximum bounce. Maximum comfort. Maximum “I could run forever.”', w: { newyork: 4, london: 1, capetown: 1 } },
      { label: 'Light, fast and aggressive. If it doesn’t feel speedy, I don’t want it.', w: { chicago: 3, berlin: 2, tokyo: 1 } },
      { label: 'A little bit of everything. Fast when I need it, comfortable when I don’t.', w: { boston: 3, london: 2, tokyo: 1 } },
      { label: 'Something protective and reliable. My feet have been through enough already.', w: { sydney: 3, capetown: 2, newyork: 1 } }
    ]}
  ];

  /* CLASS PREFIX FOR THE ELEMENTS THIS FILE CREATES (answer buttons, similarity bars,
     the NEW badge). It was hardcoded 'spq', which is sportplus's prefix — so on athlo.sg,
     whose section styles `hubwmm__*`, the answers rendered as bare buttons and the bars
     had no height. Never seen, because that page had never rendered. The host section
     owns its class names, so it names the prefix. */
  var P = CFG.classPrefix || 'spq';
  var $ = function (sel) { return root.querySelector(sel); };
  var step = 0, answers = [], result = null, loading = false;
  /* Bumped by every new result and every retake. An email POST remembers the run it was sent
     for and, if the person has moved on by the time it answers, changes nothing on screen. */
  var run = 0;

  function show(which) {
    ['intro', 'quiz', 'loading', 'gate', 'result'].forEach(function (s) {
      var el = $('[data-screen="' + s + '"]');
      if (el) el.hidden = (s !== which);
    });
    /* scrollIntoView, not scrollTo(top - 20): it honours the host's scroll-margin-top, so
       each site clears its own sticky header (the fixed -20 left sportplus's header sitting
       over the partner logos). The offset is the host's business, not the engine's. */
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.scrollIntoView({ block: 'start', behavior: still ? 'auto' : 'smooth' });
    focusScreen(which);
  }

  /* Move focus INTO the screen that just appeared. Without this, keyboard and screen-reader
     users were left on a button inside a screen that had just been hidden, and nothing
     announced the new question or the result. preventScroll: the scroll above owns that. */
  function focusScreen(which) {
    var scr = $('[data-screen="' + which + '"]');
    var el = scr && scr.querySelector('[data-screen-focus]');
    if (!el) return;
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (e) {}
  }

  function score() {
    var t = {}; Object.keys(MAJORS).forEach(function (k) { t[k] = 0; });
    answers.forEach(function (ai, qi) {
      var w = QUESTIONS[qi].opts[ai].w;
      Object.keys(w).forEach(function (k) { t[k] += w[k]; });
    });
    var max = Math.max.apply(null, Object.keys(t).map(function (k) { return t[k]; }));
    var winner = TIEBREAK.filter(function (k) { return t[k] === max; })[0];
    var ranked = Object.keys(t).sort(function (a, b) {
      return t[b] - t[a] || TIEBREAK.indexOf(a) - TIEBREAK.indexOf(b);
    });
    return { tally: t, winner: winner, ranked: ranked, max: max || 1 };
  }

  /* ------------------------------ questions ------------------------------ */
  function renderQ() {
    var q = QUESTIONS[step];
    $('[data-q-topic]').textContent = q.topic;
    $('[data-q-text]').textContent = q.q;
    $('[data-q-num]').textContent = (step + 1) + ' / ' + QUESTIONS.length;
    $('[data-q-fill]').style.width = (step / QUESTIONS.length * 100) + '%';
    $('[data-back]').style.visibility = step === 0 ? 'hidden' : 'visible';
    lockUntil = Date.now() + 350;       /* a double-tap must not answer the NEXT question too */

    var box = $('[data-q-opts]');
    box.innerHTML = '';
    q.opts.forEach(function (o, i) {
      var b = document.createElement('button');
      b.className = P + '__opt';
      b.type = 'button';
      /* A–D letter badges, not emoji: the brief asked for "icon-led", but a row of phone
         emoji read as template-generated next to a client's own race photography. */
      var ic = document.createElement('span'); ic.className = P + '__ic'; ic.setAttribute('aria-hidden', 'true');
      ic.textContent = 'ABCDEFGH'.charAt(i);
      var tx = document.createElement('span'); tx.textContent = o.label;   /* textContent: copy is never trusted as HTML */
      b.appendChild(ic); b.appendChild(tx);
      if (answers[step] === i) b.setAttribute('aria-current', 'true');
      b.addEventListener('click', function () { pick(i); });
      box.appendChild(b);
    });
    if (!$('[data-screen="quiz"]').hidden) focusScreen('quiz');
  }

  var lockUntil = 0;
  function pick(i) {
    if (loading || Date.now() < lockUntil) return;
    answers[step] = i;
    if (step < QUESTIONS.length - 1) { step++; renderQ(); } else { finish(); }
  }

  /* ------------------------------- result -------------------------------- */
  /* Draw everything about a result into the (still hidden) result screen. Shared by a
     finished quiz and a card link re-opened from the email (restoreFromCard). */
  function renderResult() {
    var m = MAJORS[result.winner];
    preloadFor(result.winner);

    $('[data-q-fill]').style.width = '100%';
    var cityEl = $('[data-r-city]');
    cityEl.textContent = m.city;
    if (m.isNew) {
      var b = document.createElement('span'); b.className = P + '__badge'; b.textContent = copy('newBadge', 'New');
      cityEl.appendChild(b);
    }
    $('[data-r-persona]').textContent = m.personaLabel;
    $('[data-r-hook]').textContent = m.hookLabel;

    var sim = $('[data-r-sim]'); sim.innerHTML = '';
    var fills = [];
    result.ranked.forEach(function (k, idx) {
      var pct = Math.round(result.tally[k] / result.max * 100);
      var row = document.createElement('div');
      row.className = P + '__sim' + (idx === 0 ? ' is-top' : '');
      var c = document.createElement('div'); c.className = P + '__sim-city'; c.textContent = MAJORS[k].city;
      var tr = document.createElement('div'); tr.className = P + '__sim-track';
      var fl = document.createElement('div'); fl.className = P + '__sim-fill';
      var p = document.createElement('div'); p.className = P + '__sim-pct'; p.textContent = pct + '%';
      tr.appendChild(fl); row.appendChild(c); row.appendChild(tr); row.appendChild(p);
      sim.appendChild(row);
      fills.push([fl, pct]);
    });

    renderBridge(m, result);
    renderVisuals(m, result.winner, result);
    renderShoe();
    redrawCards();
    pendingFills = fills;
  }

  function finish() {
    run++;
    result = score();
    renderResult();
    track(MAJORS[result.winner], result);

    /* The answers are ALREADY recorded (track() above) before any email is asked for, so
       the client receives every response whether or not this person goes on to join. */
    runLoader(function () {
      if (teaserOn()) openTeaser(); else if (gateRequired()) openGate(); else reveal();
    });
  }

  /* The bars are filled AFTER the result is on screen: a width transition does not run
     on an element that is display:none, so filling them behind the loading or email
     screen would make them simply appear full. */
  var pendingFills = [];
  function reveal() {
    /* Re-checked NOW, not only at the last answer: someone who sat on the email step across
       the close must not be shown "the ballot is open" (review, 2026-09-29). */
    if (result) { renderBridge(MAJORS[result.winner], result); redrawCards(); }
    show('result');
    /* Locked (teaser): the match bars are display:none, and a width transition does not run on
       an element that is not rendered — fill them at the unlock, or they would just appear full. */
    if (isLocked()) return;
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        pendingFills.forEach(function (f) { f[0].style.width = f[1] + '%'; });
      });
    });
  }

  /* ------------------------------ visuals ---------------------------------
     The client's own photography, medal and logo for the result, plus the course
     comparison ("Sydney read against the field", per the brief). Every element lives
     inside a [data-vis] box that is HIDDEN when its asset is missing, so a Major with no
     medal photo shows no medal slot rather than a broken-image icon. */
  function setImg(sel, url, alt) {
    var el = $(sel); if (!el) return null;
    var box = el.closest('[data-vis]') || el;
    if (!url) { box.hidden = true; el.removeAttribute('src'); return null; }
    /* A picture that fails to load hides its box too — never a broken-image icon. */
    el.onerror = function () { if (el.getAttribute('src') === url) box.hidden = true; };
    el.alt = alt || ''; el.src = url; box.hidden = false;
    return el;
  }

  /* THE ASICS SHOE MATCH (client, 2026-09-29). The answer to the shoe question, A–D, picks one
     of four shoes; names and pictures come from the section (CFG.shoes, in answer order), so the
     line-up can change in the theme editor — it already did once (Novablast → Sonicblast).
     Shown before the email. No name for that answer = no card; no picture = the card without one.
     It states only what is true: which answer picked it, never a claim about the shoe. */
  function renderShoe() {
    var box = $('[data-shoe]'); if (!box) return;
    var qi = -1;
    QUESTIONS.forEach(function (q, i) { if (q.id === 'shoe') qi = i; });
    var ai = qi > -1 ? answers[qi] : null;
    var s = ai != null ? (CFG.shoes || [])[ai] : null;
    var name = s && String(s.name || '').trim();
    if (!name) { box.hidden = true; return; }
    $('[data-shoe-name]').textContent = name;
    var ans = $('[data-shoe-answer]'); if (ans) ans.textContent = QUESTIONS[qi].opts[ai].label;
    setImg('[data-shoe-img]', s.img || '', 'ASICS ' + name);
    box.hidden = false;
  }

  function renderVisuals(m, key, res) {
    var ph = setImg('[data-r-photo]', asset(key, 'photo'), m.race);
    if (ph) ph.style.objectPosition = Math.round(m.fx * 100) + '% ' + Math.round(m.fy * 100) + '%';
    var lg = setImg('[data-r-logo]', asset(key, 'logo'), m.race);
    if (lg) (lg.closest('[data-vis]') || lg).style.background = m.logoBg || '#fff';
    setImg('[data-r-medal]', asset(key, 'medal'), m.race + ' finisher medal');
    var cap = $('[data-r-medal-cap]'); if (cap) cap.textContent = copy('medalCaption', 'The {race} finisher medal', { race: m.race });

    /* Your course against Sydney's — or, if you ARE Sydney, Sydney against your
       runner-up, so the comparison is never a strip compared with itself. */
    var block = $('[data-elev]');
    if (!block) return;
    var a = key === 'sydney' ? 'sydney' : key, b = key === 'sydney' ? res.ranked[1] : 'sydney';
    var ua = asset(a, 'elev'), ub = asset(b, 'elev');
    if (!ua || !ub) { block.hidden = true; return; }
    block.hidden = false;
    var t = $('[data-elev-title]');
    if (t) t.textContent = key === 'sydney' ? copy('elevTitleSydney', 'Sydney against your runner-up') : copy('elevTitle', 'Your course against Sydney');
    setImg('[data-elev-a]', ua, MAJORS[a].race + ' course elevation profile');
    setImg('[data-elev-b]', ub, MAJORS[b].race + ' course elevation profile');
    /* The strips carry their numbers as small print, so each one opens full size on tap. */
    var ka = $('[data-elev-a-link]'), kb = $('[data-elev-b-link]');
    if (ka) ka.href = ua; if (kb) kb.href = ub;
    var la = $('[data-elev-a-label]'), lb = $('[data-elev-b-label]');
    if (la) la.textContent = copy('elevLabelYours', 'Your Major · {race}', { race: MAJORS[a].race });
    if (lb) lb.textContent = key === 'sydney' ? copy('elevLabelRunnerUp', 'Your runner-up · {race}', { race: MAJORS[b].race })
                                              : copy('elevLabelCompared', 'Compared with · {race}', { race: MAJORS[b].race });
  }

  /* ----------------------------- loading beat -----------------------------
     THE SPONSOR MOMENT. A short "working it out" screen between the last answer and
     the result — the one screen every single taker sees, at the moment they are most
     invested, which is why the host section puts the presenting-partner credit on it.
     The engine only drives the timing and the rotating line; WHAT the screen says and
     whose name is on it is markup, owned by the section.

     Kept honest and short: the result is already computed (and tracked) before this
     starts, so it is pure pacing, capped at ~2.6s. No [data-screen="loading"] in the
     markup = no delay at all, so a host that does not want it simply leaves it out.
     ----------------------------------------------------------------------- */
  var LOAD_LINES = ['Analysing your answers', 'Comparing all eight Majors', 'Matching your runner persona', 'Preparing your result'];
  var LOAD_STEP_MS = 650;

  function runLoader(done) {
    var screen = $('[data-screen="loading"]');
    if (!screen) { done(); return; }
    /* A theme-editor textarea arrives as one string, one line per message. */
    var lines = CFG.loadingLines;
    if (typeof lines === 'string') lines = lines.split(/\r?\n/);
    lines = (lines || []).map(function (s) { return String(s).trim(); }).filter(Boolean).slice(0, 4);
    if (!lines.length) lines = LOAD_LINES;
    var msg = $('[data-load-msg]'), fill = $('[data-load-fill]');
    var total = lines.length * LOAD_STEP_MS;

    loading = true;
    if (fill) {
      fill.style.transition = 'none'; fill.style.width = '0';
      void fill.offsetWidth;                         /* commit 0 so a retake animates again */
      fill.style.transition = 'width ' + total + 'ms linear';
    }
    show('loading');
    if (fill) requestAnimationFrame(function () { fill.style.width = '100%'; });

    var i = 0;
    (function next() {
      if (i < lines.length) {
        if (msg) msg.textContent = lines[i];
        i++; setTimeout(next, LOAD_STEP_MS);
      } else {
        loading = false;
        done();
      }
    })();
  }

  /* THE #2 BRIDGE. Sydney wins 16.6% of paths but places top-three on 48.3%.
     For the ~32% who came close without winning, the CTA can say something true
     about THEM rather than shouting a generic offer — which converts better than
     rigging the scoring AND cannot be caught out. The last branch matters just as
     much: when Sydney genuinely is not their match, the copy stops pretending. */
  /* THE BALLOT STATE. Every "open" statement on the page is paired with a closed version,
     and this flips them at the published close (10am AEDT 19 Oct = 23:00 UTC 18 Oct), with
     no deploy and nobody awake. A page that says "the ballot is open" the morning after it
     shut is a false statement made on the client's behalf. */
  function ballotClosed() {
    var t = CFG.ballotCloseAt ? Date.parse(CFG.ballotCloseAt) : NaN;
    return !isNaN(t) && Date.now() >= t;
  }
  function applyBallotState() {
    var closed = ballotClosed();
    Array.prototype.forEach.call(root.querySelectorAll('[data-when-open]'), function (el) { el.hidden = closed; });
    Array.prototype.forEach.call(root.querySelectorAll('[data-when-closed]'), function (el) { el.hidden = !closed; });
    return closed;
  }
  applyBallotState();
  /* ...and AT the close, for a page that is simply left open across it (a locked teaser invites
     exactly that: it shows the ballot while someone decides about the email). A background tab
     may have its timers throttled, so returning to the tab re-checks as well. */
  function recheckBallot() {
    if (result) { renderBridge(MAJORS[result.winner], result); redrawCards(); } else applyBallotState();
  }
  (function () {
    var t = CFG.ballotCloseAt ? Date.parse(CFG.ballotCloseAt) : NaN, wait = t - Date.now();
    if (wait > 0 && wait < 2147483647) setTimeout(recheckBallot, wait + 1000);
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') recheckBallot(); });
  })();

  /* THE #2 BRIDGE. Sydney wins 16.6% of paths but places top-three on 48.3%. For the ~32%
     who came close without winning, the copy can say something true about THEM. It states
     only what the fact-check confirmed (29 Sep 2026): the ballot is open until the close,
     there is no qualifying time, entry is free and you are charged only if drawn. It does
     NOT say Sydney is the only Major open — Berlin and Chicago both open during the window. */
  var ORDINAL = ['first', 'second', 'third'];

  /* Each open-ballot version reads its heading and copy through copy() (see EDITABLE WORDING),
     with {city} {close} {place} {terms} filled in. */
  function renderBridge(m, res) {
    var closed = applyBallotState();
    var terms = COPY.ballotTerms || 'There’s no qualifying time, and it’s free to enter: you’re only charged if you’re drawn.';
    var rank = res.ranked.indexOf('sydney');
    var pct = Math.round(res.tally.sydney / res.max * 100);
    var place = (rank === 1 || rank === 2) ? (pct === 100 ? 'level with it' : ORDINAL[rank] + ' at ' + pct + '%') : '';
    var kicker, text, tpl;
    if (closed) {
      kicker = CFG.closedKicker || 'The 2027 ballot has closed';
      text = CFG.closedText || 'Ballot results are released on 3 Nov 2026.';
    } else {
      if (rank === 0) {
        kicker = COPY.ballotKickerMatch || 'And its ballot is open now';
        tpl = COPY.ballotTextMatch || 'Good news: the TCS Sydney Marathon 2027 ballot is open until {close}. {terms}';
      } else if (rank === 1 || rank === 2) {
        kicker = COPY.ballotKickerTop3 || 'One of your top three is open now';
        tpl = COPY.ballotTextTop3 || 'Your Major is {city}, and Sydney came {place}. Its 2027 ballot is open until {close}. {terms}';
      } else {
        kicker = COPY.ballotKickerOther || 'A Major you can enter now';
        tpl = COPY.ballotTextOther || 'Your Major is {city}. The TCS Sydney Marathon 2027 ballot is open until {close}. {terms}';
      }
      // No close date set: drop the clause, exactly as the old ' until ' + label did.
      if (!CFG.closeLabel) tpl = String(tpl).replace(/ until \{close\}/g, '');
      text = fillCopy(tpl, { city: m.city, close: CFG.closeLabel || '', place: place, terms: terms });
    }
    $('[data-ballot-kicker]').textContent = kicker;
    $('[data-bridge]').textContent = text;
  }

  /* --------------------------------- card ---------------------------------
     9:16 IS PRIMARY. The prize mechanic is "share to IG Stories", and a 4:5 card
     posted to a Story gets letterboxed with grey bars — which reads as broken and
     is the fastest way to kill the sharing this campaign runs on.
     ANSWERS, NEVER PIXELS: the card is drawn in the browser from the answer set
     and never uploaded. ATHLO+'s capture route refuses image payloads outright,
     because a storefront-reachable "mail this picture to that address" endpoint
     would be a spam relay on a DMARC-aligned domain. Same rule here.
     ----------------------------------------------------------------------- */
  /* THE CARD IS A PHOTO CARD NOW (client assets, 2026-09-29): the Major's own race photo
     fades down into its colour, the city sits over it, and the finisher medal is the
     centrepiece — the brief asked for "medal imagery on every answer card".
     Everything stays inside Instagram's Story safe area where it matters: nothing
     essential above ~10% (profile header) and the credit/brand below 85% are the only
     things the reply bar may cover. */
  /* INSTAGRAM SAFE ZONES decide this layout (review, 2026-09-29). A Story is overlaid by
     the poster's name and progress bars across roughly the top 12%, and by the reply bar
     across the bottom ~12%. So the partner lockup sits just BELOW the top band (it was at
     the bottom, under the reply bar, and too small to read "presented by asics" on a
     phone), and nothing that matters sits below the dates line. The feed crop has no
     overlays and follows the same order, tighter. */
  var LAYOUT = {
    story: { photoH: .47, lock: .135, lockH: .092, kicker: .262, city: .352, citeSize: .118, persona: .402,
             medal: .425, medalS: .138, hook: .610, hookSize: .031, hookLead: .045, hookWide: .80,
             matchLbl: .718, match: .752, cta: .810, dates: .842, sponsor: .180, brand: .952 },
    feed:  { photoH: .44, lock: .045, lockH: .098, kicker: .190, city: .318, citeSize: .105, persona: .368,
             medal: .392, medalS: .150, hook: .592, hookSize: .029, hookLead: .042, hookWide: .80,
             matchLbl: .712, match: .748, cta: .818, dates: .853, sponsor: .090, brand: .958 }
  };

  /* ---- images on the card ----
     A canvas that has drawn a cross-origin image is TAINTED, and toBlob() on it throws —
     which would silently kill the Stories share this campaign runs on. In production
     every image is a theme asset served from the storefront's own origin, so this never
     bites; but "never" is a claim about hosting, not about this file. So every image is
     loaded CORS-mode and PROBED on a 1x1 canvas before it is allowed near the card. One
     that fails the probe is simply left off: the card falls back to its colour, and to
     a text credit instead of the lockup. A card without a photo still shares; a card
     that throws does not. */
  var A = CFG.assets || {};
  function asset(key, kind) { var m = A.majors && A.majors[key]; return (m && m[kind]) || ''; }

  var IMG = {};
  function img(url) {
    if (!url) return null;
    var rec = IMG[url];
    if (!rec) {
      rec = IMG[url] = { el: new Image(), ok: false };
      rec.el.crossOrigin = 'anonymous';
      rec.el.decoding = 'async';
      rec.el.onload = function () { rec.ok = drawable(rec.el); if (rec.ok) redrawCards(); };
      rec.el.src = url;
    }
    return rec.ok ? rec.el : null;
  }
  function drawable(el) {
    try {
      var c = document.createElement('canvas'); c.width = c.height = 1;
      var cx = c.getContext('2d'); cx.drawImage(el, 0, 0, 1, 1); cx.getImageData(0, 0, 1, 1);
      return true;
    } catch (e) { return false; }
  }
  /* ISOLATED. A canvas can fail in ways outside our control (WebKit returns a null context
     once a page passes its canvas-memory cap). The card is a nice-to-have; the result, the
     tracking row and the membership step are not — so a drawing failure is contained here
     and can never strand someone on question seven. */
  function redrawCards() {
    if (!result) return;
    var m = MAJORS[result.winner];
    try { drawCard($('[data-card-story]'), m, result, 'story'); } catch (e) {}
    try { drawCard($('[data-card-feed]'), m, result, 'feed'); } catch (e) {}
  }
  /* Start fetching this result's card images the moment it is known — they load during
     the ~2.6s loading beat, so the card is usually complete by the time it is seen. */
  function preloadFor(key) { img(asset(key, 'photo')); img(asset(key, 'medal')); img(A.lockup); }

  /* Draw `el` to fill the box, cropped around its focal point rather than its centre. */
  function cover(x, el, dx, dy, dw, dh, fx, fy) {
    var iw = el.naturalWidth, ih = el.naturalHeight, sc = Math.max(dw / iw, dh / ih);
    var sw = dw / sc, sh = dh / sc;
    var sx = Math.min(Math.max(iw * (fx == null ? .5 : fx) - sw / 2, 0), iw - sw);
    var sy = Math.min(Math.max(ih * (fy == null ? .5 : fy) - sh / 2, 0), ih - sh);
    x.drawImage(el, sx, sy, sw, sh, dx, dy, dw, dh);
  }
  function roundRect(x, rx, ry, w, h, r) {
    x.beginPath();
    x.moveTo(rx + r, ry); x.arcTo(rx + w, ry, rx + w, ry + h, r); x.arcTo(rx + w, ry + h, rx, ry + h, r);
    x.arcTo(rx, ry + h, rx, ry, r); x.arcTo(rx, ry, rx + w, ry, r); x.closePath();
  }

  function drawCard(canvas, m, res, fmt) {
    if (!canvas) return;
    var x = canvas.getContext('2d');
    if (!x) return;
    var W = canvas.width, H = canvas.height, L = LAYOUT[fmt];
    var F = '"Inter",-apple-system,Helvetica,Arial,sans-serif';
    var px = function (f) { return Math.round(W * f); };
    var y = function (f) { return Math.round(H * f); };
    var key = res.winner;
    x.save();
    x.clearRect(0, 0, W, H);

    /* ground: the Major's colour, darkened, with a vignette */
    var g = x.createLinearGradient(0, 0, W * 0.6, H);
    g.addColorStop(0, m.tone[0]); g.addColorStop(1, m.tone[1]);
    x.fillStyle = g; x.fillRect(0, 0, W, H);
    x.fillStyle = 'rgba(0,0,0,.38)'; x.fillRect(0, 0, W, H);
    var v = x.createRadialGradient(W / 2, H * 0.45, W * 0.1, W / 2, H * 0.55, H * 0.85);
    v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,.5)');
    x.fillStyle = v; x.fillRect(0, 0, W, H);

    /* the race photo, faded into the ground through an alpha mask (not a black overlay,
       which would leave a visible seam where the photo ends) */
    var photo = img(asset(key, 'photo')), bandH = y(L.photoH);
    if (photo) {
      var off = document.createElement('canvas'); off.width = W; off.height = bandH;
      var ox = off.getContext('2d');
      cover(ox, photo, 0, 0, W, bandH, m.fx, m.fy);
      var mask = ox.createLinearGradient(0, 0, 0, bandH);
      mask.addColorStop(0, 'rgba(0,0,0,1)'); mask.addColorStop(.55, 'rgba(0,0,0,1)'); mask.addColorStop(1, 'rgba(0,0,0,0)');
      ox.globalCompositeOperation = 'destination-in'; ox.fillStyle = mask; ox.fillRect(0, 0, W, bandH);
      x.drawImage(off, 0, 0);
      /* legibility: a scrim behind the kicker, and a lift behind the city name */
      var sH = y(L.kicker) + px(.06);
      var s1 = x.createLinearGradient(0, 0, 0, sH);
      s1.addColorStop(0, 'rgba(0,0,0,.62)'); s1.addColorStop(.7, 'rgba(0,0,0,.45)'); s1.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = s1; x.fillRect(0, 0, W, sH);
      var s2 = x.createLinearGradient(0, y(L.city) - px(.16), 0, y(L.persona) + px(.03));
      s2.addColorStop(0, 'rgba(0,0,0,0)'); s2.addColorStop(.6, 'rgba(0,0,0,.42)'); s2.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = s2; x.fillRect(0, y(L.city) - px(.16), W, y(L.persona) - y(L.city) + px(.19));
    }

    x.textAlign = 'center';

    /* Partner credit: the official "TCS Sydney Marathon presented by ASICS" lockup when it
       is available and drawable, the same words as text when it is not. */
    var lock = img(A.lockup);
    if (lock) {
      var lh = y(L.lockH), lw = lh * lock.naturalWidth / lock.naturalHeight;
      x.drawImage(lock, (W - lw) / 2, y(L.lock), lw, lh);
    } else if (CFG.cardSponsor) {
      x.fillStyle = 'rgba(255,255,255,.85)'; x.font = '700 ' + px(.022) + 'px ' + F;
      x.letterSpacing = Math.round(W * .003) + 'px';
      x.fillText(String(CFG.cardSponsor).toUpperCase(), W / 2, y(L.sponsor)); x.letterSpacing = '0px';
    }

    x.fillStyle = 'rgba(255,255,255,.82)'; x.font = '600 ' + px(.025) + 'px ' + F;
    x.letterSpacing = Math.round(W * .005) + 'px';
    x.fillText('YOUR WORLD MARATHON MAJOR', W / 2, y(L.kicker));
    x.letterSpacing = '0px';

    /* Long names ("New York City", "Cape Town") step down rather than run off the edge. */
    x.shadowColor = 'rgba(0,0,0,.45)'; x.shadowBlur = px(.03);
    var city = m.city.toUpperCase(), size = px(L.citeSize);
    do { x.font = '800 ' + size + 'px ' + F; size -= 4; }
    while (x.measureText(city).width > W * 0.88 && size > px(.05));
    x.fillStyle = '#fff'; x.fillText(city, W / 2, y(L.city));
    x.fillStyle = 'rgba(255,255,255,.95)'; x.font = '650 ' + px(.045) + 'px ' + F;
    x.fillText(m.personaLabel, W / 2, y(L.persona));
    x.shadowColor = 'transparent'; x.shadowBlur = 0;

    /* the medal: a framed square, because not every Major's medal is round */
    var medal = img(asset(key, 'medal'));
    if (medal) {
      var ms = y(L.medalS), mx = (W - ms) / 2, my = y(L.medal), r = Math.round(ms * .14);
      x.save();
      x.shadowColor = 'rgba(0,0,0,.5)'; x.shadowBlur = px(.04); x.shadowOffsetY = px(.012);
      roundRect(x, mx, my, ms, ms, r); x.fillStyle = '#111'; x.fill();
      x.shadowColor = 'transparent';
      x.clip(); x.drawImage(medal, mx, my, ms, ms);
      x.restore();
      roundRect(x, mx, my, ms, ms, r); x.lineWidth = Math.max(3, px(.005));
      x.strokeStyle = 'rgba(255,255,255,.85)'; x.stroke();
    }

    x.fillStyle = 'rgba(255,255,255,.88)'; x.font = '400 ' + px(L.hookSize) + 'px ' + F;
    wrap(x, m.hookLabel, W / 2, y(L.hook), W * L.hookWide, px(L.hookLead));

    x.fillStyle = 'rgba(255,255,255,.55)'; x.font = '600 ' + px(.022) + 'px ' + F;
    x.letterSpacing = Math.round(W * .003) + 'px';
    x.fillText('CLOSEST MATCHES', W / 2, y(L.matchLbl)); x.letterSpacing = '0px';
    x.font = '600 ' + px(.028) + 'px ' + F; x.fillStyle = 'rgba(255,255,255,.9)';
    x.fillText(res.ranked.slice(0, 3).map(function (k) {
      return MAJORS[k].city + ' ' + Math.round(res.tally[k] / res.max * 100) + '%';
    }).join('   ·   '), W / 2, y(L.match));

    x.fillStyle = 'rgba(255,255,255,.96)'; x.font = '700 ' + px(.032) + 'px ' + F;
    var shut = ballotClosed();
    x.fillText(shut ? (CFG.cardCtaClosed || 'TCS Sydney Marathon 2027') : (CFG.cardCta || 'Enter the Sydney 2027 ballot'), W / 2, y(L.cta));
    x.fillStyle = 'rgba(255,255,255,.62)'; x.font = '400 ' + px(.025) + 'px ' + F;
    x.fillText(shut ? (CFG.cardDatesClosed || 'Race day 29 Aug 2027') : (CFG.cardDates || 'Ballot open until 19 Oct, 7am SGT'), W / 2, y(L.dates));

    /* NO DEFAULT BRAND, ON PURPOSE. This used to fall back to a hardcoded
       'SPORTPLUS.SG', which is fine on one storefront and wrong on the other —
       a blank setting on athlo.sg would have printed the other site's name on
       an athlete's shareable card. The host section owns its own brand line;
       if it supplies none, the card simply carries none. Caught by
       _verify/wmm-quiz-parity-test.mjs, which refuses a store hostname in the
       shared engine precisely because that is the first step toward two. */
    if (CFG.cardBrand) {
      x.fillStyle = 'rgba(255,255,255,.45)'; x.font = '600 ' + px(.023) + 'px ' + F;
      x.letterSpacing = Math.round(W * .003) + 'px';
      x.fillText(CFG.cardBrand, W / 2, y(L.brand)); x.letterSpacing = '0px';
    }
    x.restore();
  }

  function wrap(x, text, cx, y0, maxW, lh) {
    var words = String(text).split(' '), line = '', n = 0;
    for (var i = 0; i < words.length; i++) {
      var test = line ? line + ' ' + words[i] : words[i];
      if (x.measureText(test).width > maxW && line) { x.fillText(line, cx, y0 + n * lh); line = words[i]; n++; }
      else { line = test; }
    }
    if (line) x.fillText(line, cx, y0 + n * lh);
  }

  /* Web Share first, download second. `a.download` is unreliable on iOS Safari
     (it opens the PNG in a tab instead of saving), and this campaign is mostly
     phone traffic with a Stories mechanic — so a save that silently does the
     wrong thing on iPhone would take out the whole sharing loop. Sharing a File
     opens the native sheet with Instagram already in it: more reliable AND fewer
     taps than save-then-find-it-in-Photos. */
  function saveCard(canvas, filename) {
    if (!canvas || !canvas.toBlob) { return; }
    canvas.toBlob(function (blob) {
      if (!blob) return;
      try {
        var file = new File([blob], filename, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file] }).catch(function () { /* dismissed, not an error */ });
          return;
        }
      } catch (e) { /* no File constructor; fall through to download */ }
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.download = filename; a.href = url; a.click();
      setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
      /* A download on a laptop is a dead end for a Stories mechanic unless someone says
         what to do next — Instagram only posts Stories from the phone app. */
      var note = $('[data-share-note]');
      if (note) note.textContent = CFG.savedHint ||
        'Card saved. Instagram only posts Stories from the app, so send it to your phone and add it from there.';
    }, 'image/png');
  }

  /* ------------------------------ share links -----------------------------
     INSTAGRAM HAS NO WEB SHARE LINK. There is no URL a website can open that lands in
     the Stories composer (the instagram-stories:// scheme is for registered native apps
     only). So the Instagram buttons are saveCard() above: on a phone that opens the
     share sheet with the image attached and Instagram in it, which is the shortest path
     a web page can offer. Everything below is a plain link-share for the networks that
     DO accept one.

     Every link carries utm_source=<network>, and the quiz's own response tracking
     already records location.search — so the sheet shows which network each taker
     arrived from with no extra plumbing.
     ----------------------------------------------------------------------- */
  var enc = encodeURIComponent;
  var NETWORKS = {
    whatsapp: function (u, t) { return 'https://wa.me/?text=' + enc(t + ' ' + u); },
    telegram: function (u, t) { return 'https://t.me/share/url?url=' + enc(u) + '&text=' + enc(t); },
    facebook: function (u)    { return 'https://www.facebook.com/sharer/sharer.php?u=' + enc(u); },
    x:        function (u, t) { return 'https://x.com/intent/post?text=' + enc(t) + '&url=' + enc(u); },
    threads:  function (u, t) { return 'https://www.threads.net/intent/post?text=' + enc(t + ' ' + u); }
  };

  /* ?view= is KEPT: if the quiz is being served through Shopify's alternate-template
     parameter rather than its own Page, dropping it sent every shared link to a plain page
     with no quiz on it — the share loop dead, and nothing erroring. */
  function shareLink(src) {
    var base = location.href.split('#')[0].split('?')[0];
    var view = (location.search.match(/[?&]view=([\w-]+)/) || [])[1];
    return base + '?' + (view ? 'view=' + enc(view) + '&' : '') + 'wmm=' + enc(result.winner) +
           '&utm_source=' + enc(src) + '&utm_medium=social&utm_campaign=wmm_quiz';
  }

  function tmpl(tpl, m) {
    return String(tpl).replace(/\{city\}/g, m.city).replace(/\{persona\}/g, m.personaLabel);
  }

  function shareText() {
    return tmpl(CFG.shareText || 'I’m {city}: {persona}. Which World Marathon Major are you?', MAJORS[result.winner]);
  }

  /* ARRIVING FROM A SHARED LINK. ?wmm=<major> has been on every copied link since day
     one and nothing read it, so a friend who tapped through landed on a cold intro. Now
     the intro says who sent them and what they got — the "beat your mate" hook is the
     whole reason a shared result converts. Only a known key is honoured, and it is set
     with textContent, so a hand-edited link cannot inject anything. */
  (function sharedArrival() {
    var el = $('[data-shared]');
    var key = (location.search.match(/[?&]wmm=([a-z]+)/) || [])[1];
    if (!el || !key || !Object.prototype.hasOwnProperty.call(MAJORS, key)) return;
    el.textContent = tmpl(CFG.sharedLine || 'Your friend is {city}: {persona}. Which Major are you?', MAJORS[key]);
    el.hidden = false;
  })();

  /* ------------------------ response tracking ----------------------------
     Fire-and-forget to the client's own Google Apps Script, writing to a Sheet
     in their Drive. text/plain avoids a CORS preflight Apps Script cannot
     answer; no-cors because we never read the reply; keepalive because the page
     may navigate straight after. A tracking failure must never reach the
     athlete, hence the bare catch.
     ---------------------------------------------------------------------- */
  function respondent() {
    var id = '', n = 1;
    try {
      var ls = window.localStorage;
      id = ls.getItem('wmm:rid') || '';
      if (!/^[0-9a-f]{16}$/.test(id)) {
        var a = new Uint8Array(8); (window.crypto || window.msCrypto).getRandomValues(a);
        id = Array.prototype.map.call(a, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
        ls.setItem('wmm:rid', id);
      }
      n = (parseInt(ls.getItem('wmm:n') || '0', 10) || 0) + 1;
      ls.setItem('wmm:n', String(n));
    } catch (e) { /* storage blocked: the row still goes, just without a stable id */ }
    return { id: id, n: n };
  }

  function track(m, res) {
    if (!CFG.trackUrl || CFG.captureDryRun) return;     /* a dry run sends nothing, anywhere */
    var payload = {
      secret: CFG.trackSecret || '',
      Timestamp: new Date().toISOString(),
      Result: m.city,
      Persona: m.persona
    };
    QUESTIONS.forEach(function (q, i) {
      payload[q.topic] = (answers[i] != null) ? q.opts[answers[i]].label : '';
    });
    res.ranked.forEach(function (k, i) {
      if (i < 3) payload['Match ' + (i + 1)] = MAJORS[k].city + ' ' + Math.round(res.tally[k] / res.max * 100) + '%';
    });
    /* BOTH SITES WRITE TO ONE SHEET, so every row says which one it came from — Page alone
       is /pages/wmm-quiz on both and could not tell them apart. Respondent is an anonymous
       random id kept on this device (never an email, never a customer id) so the client can
       see retakes for what they are; Attempt counts them. */
    payload.Site = String(location.hostname || '').replace(/^www\./, '');
    payload.Page = location.pathname;
    /* Only what the client needs: the referring SITE (not a full URL, which can carry other
       people's query strings) and the campaign parameters — not every tracking id an ad
       network appended to the link. */
    var ref = ''; try { ref = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) {}
    payload.Referrer = ref;
    payload.UTM = (location.search.match(/[?&](utm_[a-z]+|wmm)=[^&#]*/g) || []).map(function (p) { return p.slice(1); }).join('&');
    var who = respondent();
    payload.Respondent = who.id;
    payload.Attempt = who.n;
    try {
      fetch(CFG.trackUrl, {
        method: 'POST', mode: 'no-cors', keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      }).catch(function () {});
    } catch (e) {}
  }

  /* ------------------------ ATHLO+ membership ----------------------------
     THE EMAIL STEP IS THE MEMBERSHIP (owner decision, 2026-09-29: the quiz should make
     close to 100% of finishers ATHLO+ members). One POST does everything: it creates the
     ATHLO+ customer (or finds the existing one — members use the same email), mails the
     result card, and is the giveaway entry. There is no password and no second step.
     WHAT THE EMAIL BUYS changed the same afternoon (the owner's boss): in the default
     'teaser' mode the Major, its photo, medal and the ballot show at once and the email
     UNLOCKS the full report; 'gate' (email before any result) remains as a setting. See
     MODE below.

     The rules that make a required step fair, and safe to run for a client:
       * The answers are ALREADY in the client's sheet (track() runs before this). The
         client's data never depends on anyone giving an email.
       * Marketing consent stays a SEPARATE, unticked box. Membership is the condition of
         the unlock; marketing email never is. The route refuses a campaign capture that
         does not carry an explicit boolean, so this cannot silently default.
       * A slow or failing backend must never hold a result hostage. After a valid email
         the full result is shown whatever the server says; a capture that did not land
         (offline, rate-limited, 5xx, timeout) is queued ON THIS DEVICE and retried in
         this visit and on later ones until the server confirms it, then deleted.
       * Nothing is claimed that did not happen: the confirmation says "on its way" only
         when the server said yes, and says something softer when the capture is queued.
     `captureMode: 'optional'` is the emergency lever: the email box moves into the result
     page instead of standing in front of it. No code change needed, only the setting.
     ---------------------------------------------------------------------- */
  var QKEY = 'wmm:pending', QMAX_AGE = 7 * 864e5, submitting = false;

  function store(kind) { try { var s = window[kind]; s.setItem('wmm:probe', '1'); s.removeItem('wmm:probe'); return s; } catch (e) { return null; } }
  var LS = store('localStorage');

  /* THREE EMAIL-STEP MODES (CFG.captureMode, a theme-editor setting):
       'teaser'   (default, 2026-09-29) — the Major, its photo, logo, medal and the ballot show at
                  once; the email UNLOCKS the persona, summary, match bars, course profile and
                  share card. The owner's boss: "gated at this stage might have drawbacks… they can
                  see which major they got and some cool graphics… they have to key in their email
                  to get the full report… and ideally at this page… the enter the ballot button".
       'gate'     — the email step stands in front of the whole result (the launch version).
       'optional' — everything shows; the email box sits inside the share card.
     Anything else is treated as 'gate', the most conservative. A mode that cannot capture (no
     endpoint and not a dry run) locks NOTHING: a lock with no key is a broken page.
     A teaser whose markup is missing (an older copy of the section) falls back to 'gate'. */
  var MODE = (CFG.captureMode === 'teaser' || CFG.captureMode === 'optional') ? CFG.captureMode : 'gate';
  function canCapture() { return !!(CFG.memberUrl || CFG.captureDryRun); }
  function teaserOn() { return MODE === 'teaser' && canCapture() && !!$('[data-unlock-slot]') && !!gateForm; }
  function gateRequired() {
    return (MODE === 'gate' || (MODE === 'teaser' && !teaserOn())) && canCapture() && !!$('[data-screen="gate"]');
  }
  /* ONE attribute on the root is the whole lock; the section's CSS hides [data-lock] and shows
     [data-lock-show] while it is set. Never `hidden` on the parts: those also carry the engine's
     own "asset missing" hiding, which an unlock must not undo. */
  function isLocked() { return root.hasAttribute('data-locked'); }
  function setLocked(on) { if (on) root.setAttribute('data-locked', ''); else root.removeAttribute('data-locked'); }

  function normEmail(v) { return String(v || '').trim().replace(/\s+/g, '').toLowerCase(); }
  /* Commas and semicolons are refused as well: the mail driver splits a recipient on them, so
     such an address would be "accepted" here and never receive its card. */
  function validEmail(v) { return v.length <= 160 && /^[^@\s"<>,;]+@[^@\s"<>,;.]+(\.[^@\s"<>,;.]+)+$/.test(v); }
  function mask(v) { var i = v.indexOf('@'); return i < 1 ? v : v.charAt(0) + '•••' + v.slice(i); }

  /* THE COMMON-TYPO CATCHER. A mistyped domain is a member who never receives their card and a
     giveaway winner we cannot reach. Rules, tightened after review (2026-09-29):
       * a domain that is itself a REAL provider is never "corrected" — qq.com, msn.com, ymail.com,
         mail.com, yahoo.com.au and friends are genuine addresses, and "fixing" them would send
         the membership and card to a stranger's mailbox;
       * short domains only match at one keystroke (two letters of a 6-character domain is a
         different domain, not a typo);
       * asking is advisory: pressing the main button again means "keep what I typed". */
  var DOMAINS = ['gmail.com', 'yahoo.com', 'yahoo.com.sg', 'hotmail.com', 'outlook.com', 'icloud.com', 'live.com', 'singnet.com.sg'];
  var REAL = ['qq.com', 'msn.com', 'me.com', 'mac.com', 'gmx.com', 'ymail.com', 'mail.com', 'email.com', 'aol.com',
              'yahoo.com.au', 'yahoo.com.hk', 'yahoo.co.uk', 'yahoo.co.in', 'yahoo.co.jp', 'hotmail.sg', 'hotmail.co.uk',
              'live.com.sg', 'live.cn', 'outlook.sg', 'protonmail.com', 'proton.me', '163.com', '126.com', 'naver.com',
              'googlemail.com', 'rocketmail.com', 'starhub.net.sg', 'pacific.net.sg'];
  /* Edit distance that counts a SWAP of neighbouring letters as one edit (optimal string
     alignment) — "gmial" for "gmail" is the single most common address typo, and plain
     Levenshtein scores it as two, which the one-edit limit for short domains then missed. */
  function dist(a, b) {
    var m = a.length, n = b.length, d = [], i, j;
    for (i = 0; i <= m; i++) { d[i] = [i]; }
    for (j = 0; j <= n; j++) { d[0][j] = j; }
    for (i = 1; i <= m; i++) for (j = 1; j <= n; j++) {
      var c = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + c);
      if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
    return d[m][n];
  }
  function suggest(email) {
    var at = email.lastIndexOf('@'); if (at < 1) return '';
    var dom = email.slice(at + 1);
    if (DOMAINS.indexOf(dom) > -1 || REAL.indexOf(dom) > -1) return '';
    var best = '', bd = 99;
    DOMAINS.forEach(function (d) {
      var x = dist(dom, d), limit = d.length <= 9 ? 1 : 2;
      if (x <= limit && x < bd) { bd = x; best = d; }
    });
    return best ? email.slice(0, at + 1) + best : '';
  }

  /* SAME-DAY REPEATS ON THIS DEVICE. The server records every campaign capture (it is the
     giveaway entry) but emails only one card per address per day. The server will not say
     which case it hit (that would let anyone test which addresses took the quiz), so this
     device remembers what IT sent, keyed by a hash, never the address itself. */
  function h32(v) { var x = 2166136261; for (var i = 0; i < v.length; i++) { x ^= v.charCodeAt(i); x = Math.imul ? Math.imul(x, 16777619) : (x * 16777619) | 0; } return (x >>> 0).toString(36); }
  function sentToday(email) {
    if (!LS) return false;
    try { var m = JSON.parse(LS.getItem('wmm:sent') || '{}'); return !!m[h32(email)] && Date.now() - m[h32(email)] < 864e5; } catch (e) { return false; }
  }
  function markSent(email) {
    if (!LS) return;
    try {
      var m = JSON.parse(LS.getItem('wmm:sent') || '{}'), now = Date.now();
      Object.keys(m).forEach(function (k) { if (now - m[k] > 864e5) delete m[k]; });
      m[h32(email)] = now; LS.setItem('wmm:sent', JSON.stringify(m));
    } catch (e) {}
  }

  function captureBody(email, optin) {
    var m = MAJORS[result.winner];
    /* FIELD NAMES ARE THE ROUTE'S. `archetype` carries the Major because that is the column
       quiz_cards stores a result title in — the server turns it into TAGS, never an ATHLO+
       archetype (campaignTags / isCampaignSource in the bookings function). `persona` also
       rides in `answers`, so the hourly reconcile sweep can rebuild the tags from the row
       alone if the inline mint ever fails. */
    return {
      email: email,
      optin: optin === true,
      source: CFG.memberSource || 'sportplus-wmm',
      archetype: m.city,
      persona: m.persona,
      answers: QUESTIONS.reduce(function (acc, q, i) {
        acc[q.id] = (answers[i] != null) ? q.opts[answers[i]].label : '';
        return acc;
      }, { persona: m.persona, campaign: 'wmm-quiz-2026' })
    };
  }

  /* status 0 = never reached the server (offline, blocked, or our 12s timeout). */
  function post(body, done) {
    if (!CFG.memberUrl) { done(0); return; }
    var ctl = window.AbortController ? new AbortController() : null, finished = false;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); if (!finished) { finished = true; done(0); } }, 12000);
    try {
      fetch(CFG.memberUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctl ? ctl.signal : undefined
      }).then(function (r) {
        clearTimeout(timer); if (!finished) { finished = true; done(r.status); }
      }).catch(function () {
        /* .catch, not .then(ok, fail): it also catches a throw inside the success path, which
           would otherwise surface as an unhandled rejection (and page the error beacon). The
           `finished` flag makes sure `done` still runs exactly once either way. */
        clearTimeout(timer); if (!finished) { finished = true; done(0); }
      });
    } catch (e) { clearTimeout(timer); if (!finished) { finished = true; done(0); } }
  }
  function retryable(st) { return st === 0 || st === 408 || st === 429 || st >= 500; }

  function readQueue() {
    if (!LS) return [];
    try { var q = JSON.parse(LS.getItem(QKEY) || '[]'); return Array.isArray(q) ? q : []; } catch (e) { return []; }
  }
  function writeQueue(q) {
    if (!LS) return;
    try { if (q.length) LS.setItem(QKEY, JSON.stringify(q.slice(-5))); else LS.removeItem(QKEY); } catch (e) {}
  }
  function enqueue(body) {
    var q = readQueue();
    q.push({ id: Date.now().toString(36) + Math.random().toString(36).slice(2, 8), b: body, t: Date.now() });
    writeQueue(q);
  }
  var flushing = false;
  function flushQueue() {
    if (flushing || !CFG.memberUrl) return;
    var now = Date.now();
    var q = readQueue().filter(function (x) { return x && x.b && x.t && now - x.t < QMAX_AGE; });
    writeQueue(q);
    if (!q.length) return;
    flushing = true;
    var item = q[0];
    post(item.b, function (st) {
      flushing = false;
      if (retryable(st)) return;                     /* still not landing: keep it for later */
      writeQueue(readQueue().filter(function (x) { return (x.id || x.t) !== (item.id || item.t); }));
      flushQueue();
    });
  }
  /* A capture queued earlier gets another chance on load, when the connection comes back, and
     when the visitor returns to the tab — not only on a later visit to this exact page. */
  setTimeout(flushQueue, 2500);
  window.addEventListener('online', function () { setTimeout(flushQueue, 1500); });
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') flushQueue(); });

  var gateForm = $('[data-gate-form]');
  function gateNote(text, isError) {
    var n = $('[data-gate-note]'); if (!n) return;
    n.textContent = text || '';
    n.classList.toggle('is-error', !!isError);
  }
  function setBusy(on) {
    var b = gateForm && gateForm.querySelector('[type="submit"]'); if (!b) return;
    if (!b.getAttribute('data-label')) b.setAttribute('data-label', b.textContent);
    b.disabled = !!on; b.setAttribute('aria-busy', on ? 'true' : 'false');
    b.textContent = on ? (MODE === 'optional' ? 'Sending…' : (MODE === 'teaser' && teaserOn() ? CFG.teaserBusy || 'Unlocking…' : CFG.gateBusy) || 'Revealing…') : b.getAttribute('data-label');
  }

  function resetForm() {
    submitting = false; setBusy(false);    /* a new result never inherits a send from the last one */
    var input = gateForm && gateForm.querySelector('[name="email"]');
    /* Pre-filled ONLY for a signed-in member (the store knows who they are). Not from an earlier
       attempt on this device: a phone handed to a friend must not submit its owner's address. */
    if (input) input.value = CFG.customerEmail || '';
    var opt = gateForm && gateForm.querySelector('[name="optin"]'); if (opt) opt.checked = false;
    typoAck = ''; gateNote('');
    /* A confirmation left over from an earlier result (before a retake) must not greet this one. */
    var note = $('[data-member-note]'); if (note) { note.textContent = ''; note.hidden = true; }
  }

  function openGate() {
    resetForm();
    show('gate');
  }

  /* The teaser: the result screen, locked. Same form, same POST, same promises; the only
     difference is what stands on screen while the email is being asked for. */
  function openTeaser() {
    resetForm();
    setLocked(true);
    reveal();
  }

  function memberDone(email, how) {
    var note = $('[data-member-note]');
    if (note) {
      note.textContent =
        how === 'dry'    ? 'Preview only. Nothing was sent. On the live site this step creates a free ATHLO+ membership for ' + mask(email) + ' and emails the result card.' :
        how === 'queued' ? 'You’re in. We’re finishing your ATHLO+ membership for ' + mask(email) + '. Your result card will follow by email.' :
        how === 'again'  ? 'You’re in. Your card was already emailed to ' + mask(email) + ' earlier today, so we haven’t sent another. This result is recorded with your entry.' :
        how === 'closed' ? '' :
                           'You’re in. Your free ATHLO+ membership is ready and your result card is on its way to ' + mask(email) + '.';
      note.hidden = !note.textContent;
    }
    submitting = false; setBusy(false);
    /* Gate and teaser: this IS the reveal (the teaser unlocks, then lands on the top of the full
       result, where the confirmation now sits). Optional mode: the result is already on screen
       and the form sits inside it, so only the confirmation changes — no jump back to the top.
       EVERY outcome unlocks, the refused one included: a server that says no must never hold
       someone's result hostage, and nothing above claims a membership that did not happen. */
    if (MODE === 'optional') { var bx = $('[data-gate-box]'); if (bx) bx.hidden = true; }
    else { setLocked(false); reveal(); }
    focusNote();
  }

  /* The confirmation is the only statement of what happened to their membership and entry
     ("you're in", "queued", "already emailed today"), so it gets the focus when there is one:
     a screen reader starts there. It sits above the Major's name, which reveal() had focused. */
  function focusNote() {
    var n = $('[data-member-note]');
    if (!n || n.hidden || !n.textContent) return;
    if (!n.hasAttribute('tabindex')) n.setAttribute('tabindex', '-1');
    try { n.focus({ preventScroll: true }); } catch (e) {}
  }

  function submitGate(email, optin) {
    submitting = true; setBusy(true); gateNote('');
    /* A POST can take up to 12s, and in teaser mode "Retake" is on the same screen. If the
       person has moved on when it answers, the capture still stands (sent or queued) but the
       screen is theirs now: no unlock of a result that is gone, no jump out of a new quiz
       (review, 2026-09-29). */
    var mine = run;
    function done(how) { if (mine === run) memberDone(email, how); }
    if (CFG.captureDryRun) { setTimeout(function () { done('dry'); }, 400); return; }
    var body = captureBody(email, optin);
    var again = sentToday(email);
    post(body, function (st) {
      if (st >= 200 && st < 300) { markSent(email); done(again ? 'again' : 'ok'); return; }
      if (retryable(st)) {
        enqueue(body);
        setTimeout(flushQueue, 30000); setTimeout(flushQueue, 120000);
        done('queued');
        return;
      }
      /* Anything else is the server refusing for good (a 400 on an address that already passed
         the stricter check above means the REQUEST was refused — e.g. the route or its source
         list changed — not that the email is wrong; 403/404 mean switched off). Never hold the
         result hostage for it, never blame the person's email, never claim a membership. */
      done('closed');
    });
  }

  var typoAck = '';
  function handleSubmit() {
    if (submitting || !result || !gateForm) return;
    var input = gateForm.querySelector('[name="email"]');
    var email = normEmail(input.value);
    var optin = !!(gateForm.querySelector('[name="optin"]') || {}).checked;
    if (!validEmail(email)) {
      gateNote('Please enter a valid email address, e.g. name@example.com.', true);
      try { input.focus(); } catch (x) {}
      return;
    }
    var fix = suggest(email);
    if (fix && typoAck !== email) {
      typoAck = email;                       /* asked once: pressing the main button again = keep */
      var n = $('[data-gate-note]');
      if (n) {
        n.textContent = 'Did you mean ';
        var use = document.createElement('button'); use.type = 'button'; use.className = P + '__linkbtn';
        use.textContent = fix;
        use.addEventListener('click', function () { input.value = fix; typoAck = fix; handleSubmit(); });
        var keep = document.createElement('button'); keep.type = 'button'; keep.className = P + '__linkbtn';
        keep.textContent = 'keep ' + email;
        keep.addEventListener('click', function () { handleSubmit(); });
        n.appendChild(use); n.appendChild(document.createTextNode('? Or ')); n.appendChild(keep);
        n.classList.remove('is-error');
      }
      return;
    }
    input.value = email;
    submitGate(email, optin);
  }

  if (gateForm) {
    gateForm.addEventListener('submit', function (e) { e.preventDefault(); handleSubmit(); });
    /* Optional mode (the emergency lever): the same form, moved into the result page, with
       wording that fits a result that is already showing. Only when there is somewhere to send
       it — a form that cannot submit must not claim "you're in". */
    if (MODE === 'optional' && canCapture()) {
      var slot = $('[data-gate-inline]'), box = $('[data-gate-box]');
      if (slot && box) {
        /* The confirmation belongs beside the form it answers, not at the top of the result. */
        var mnote = $('[data-member-note]'); if (mnote) slot.parentNode.insertBefore(mnote, slot);
        slot.appendChild(box); slot.hidden = false;
        var k = box.querySelector('.' + P + '__kicker'); if (k) k.textContent = 'Keep your result';
        var ttl = $('[data-gate-title]'); if (ttl) ttl.textContent = 'Get your card by email and enter the giveaway';
        var sb = gateForm.querySelector('[type="submit"]'); if (sb) sb.textContent = 'Send my card';
        /* The agreement line must name the button that is actually there. */
        var oa = gateForm.querySelector('[data-gate-agree]');
        if (oa) oa.textContent = 'By tapping Send my card, you agree to create a free ATHLO+ membership with this email and to the giveaway terms below.';
      }
    }
    /* Teaser mode: the SAME form (one handler, one POST) moves into the unlock card on the
       result screen. Its "reveal" heading and benefits are hidden there by the section's CSS;
       the button and the agreement line take the unlock wording, which must name the button. */
    if (teaserOn()) {
      var uslot = $('[data-unlock-slot]'), ubox = $('[data-gate-box]');
      if (uslot && ubox) {
        uslot.appendChild(ubox);
        var ub = gateForm.querySelector('[type="submit"]'); if (ub && CFG.teaserCta) ub.textContent = CFG.teaserCta;
        var ua = gateForm.querySelector('[data-gate-agree]'); if (ua && CFG.teaserNotice) ua.textContent = CFG.teaserNotice;
      }
    }
  }

  /* ---------------------- re-opening a card from the email ----------------------
     The card email links to this page with ?card=<token>. The stored answers are labels keyed
     by question id; mapping them back to options re-scores the SAME result (bars, bridge, card)
     without asking for the email again and without writing another row to the client's sheet.
     If the copy has changed since and a label no longer matches, the Major alone is shown via
     the shared-result greeting rather than a wrong result. */
  (function restoreFromCard() {
    var token = (location.search.match(/[?&]card=([A-Za-z0-9_-]{16,64})/) || [])[1];
    if (!token || !CFG.memberUrl) return;
    var getUrl = CFG.memberUrl.replace(/action=quiz-card\b/, 'action=quiz-card-get') + '&card=' + encodeURIComponent(token);
    try {
      fetch(getUrl).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
        var row = d && (d.data || d);
        if (!row || !row.answers) return;
        var idx = QUESTIONS.map(function (q) {
          var want = row.answers[q.id];
          for (var i = 0; i < q.opts.length; i++) if (q.opts[i].label === want) return i;
          return -1;
        });
        if (idx.indexOf(-1) > -1) {
          var key = Object.keys(MAJORS).filter(function (k) { return MAJORS[k].city === row.archetype; })[0];
          var el = $('[data-shared]');
          if (key && el) { el.textContent = copy('noteOldResult', 'Your result: {city} ({persona}). Take it again any time.', { city: MAJORS[key].city, persona: MAJORS[key].personaLabel }); el.hidden = false; }
          return;
        }
        answers = idx; step = QUESTIONS.length - 1;
        result = score();
        renderResult();
        setLocked(false);                  /* they came from the email: this IS the unlocked report */
        var note = $('[data-member-note]');
        if (note) { note.textContent = copy('noteSavedResult', 'Your saved result. Share your card below to complete your giveaway entry.'); note.hidden = false; }
        run++;
        reveal();
        focusNote();
      }).catch(function () { /* an expired or unknown card simply leaves the quiz as it is */ });
    } catch (e) {}
  })();

  /* --------------------------------- wiring ------------------------------- */
  var elStart = $('[data-start]'), elBack = $('[data-back]'), elAgain = $('[data-again]');
  if (elStart) elStart.addEventListener('click', function () { step = 0; answers = []; renderQ(); show('quiz'); });
  if (elBack)  elBack.addEventListener('click', function () { if (step > 0) { step--; renderQ(); } });
  if (elAgain) elAgain.addEventListener('click', function () {
    run++; submitting = false; setBusy(false);
    step = 0; answers = []; result = null; setLocked(false); show('intro');
  });

  var elStory = $('[data-save-story]'), elFeed = $('[data-save-feed]');
  if (elStory) elStory.addEventListener('click', function () {
    if (!result) return;
    saveCard($('[data-card-story]'), 'my-world-marathon-major-' + result.winner + '-story.png');
  });
  if (elFeed) elFeed.addEventListener('click', function () {
    if (!result) return;
    saveCard($('[data-card-feed]'), 'my-world-marathon-major-' + result.winner + '.png');
  });

  /* DRY RUN (CFG.shareDryRun) is for the standalone review file only. That file is
     forwarded to people who have never seen the site, from a page that is not live yet,
     so a real WhatsApp share would send their friends a dead link. Instead the button
     says exactly what it WOULD send. Never set on a storefront. */
  function dryRun(what) {
    var note = $('[data-share-note]');
    if (note) note.textContent = 'Preview only. Nothing was sent. On the live site this ' + what +
      ': “' + shareText() + '” plus a link to the quiz.';
  }

  Array.prototype.forEach.call(root.querySelectorAll('[data-share]'), function (btn) {
    var net = btn.getAttribute('data-share');
    if (!NETWORKS[net]) { btn.hidden = true; return; }
    btn.addEventListener('click', function () {
      if (!result) return;
      if (CFG.shareDryRun) { dryRun('opens ' + btn.textContent.trim() + ' with'); return; }
      window.open(NETWORKS[net](shareLink(net), shareText()), '_blank', 'noopener');
    });
  });

  /* "More" = the device's own share sheet with text + link (Messenger, LINE, SMS, email
     — whatever this phone has). Hidden where the browser has no share sheet. */
  var elNative = $('[data-share-native]');
  if (elNative) {
    if (!navigator.share) elNative.hidden = true;
    else elNative.addEventListener('click', function () {
      if (!result) return;
      if (CFG.shareDryRun) { dryRun('opens your phone’s share menu with'); return; }
      navigator.share({ text: shareText(), url: shareLink('native') }).catch(function () {});
    });
  }

  var elCopy = $('[data-copy-link]');
  if (elCopy) elCopy.addEventListener('click', function () {
    if (CFG.shareDryRun) { if (result) dryRun('copies a link to your result, shared as'); return; }
    if (!result) return;
    var u = shareLink('copy');
    var btn = this, original = btn.textContent;
    function done(ok) { btn.textContent = ok ? copy('linkCopied', 'Link copied') : u; setTimeout(function () { btn.textContent = original; }, 2200); }
    if (navigator.clipboard) navigator.clipboard.writeText(u).then(function () { done(true); }, function () { done(false); });
    else done(false);
  });

  /* 1-4 to answer, Backspace to go back. Costs nothing, and makes the client's
     review runs much faster than clicking through forty times. */
  document.addEventListener('keydown', function (e) {
    var quizScreen = $('[data-screen="quiz"]');
    if (!quizScreen || quizScreen.hidden) return;
    /* Never while someone is typing somewhere else (site search, the chat widget, a
       newsletter box): digits there were answering questions, and Backspace was swallowed. */
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.repeat || e.isComposing) return;
    var t = e.target;
    if (t && t.closest && t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]')) return;
    if (e.key >= '1' && e.key <= '4') {
      var i = +e.key - 1;
      if (i < QUESTIONS[step].opts.length) pick(i);
    } else if (e.key === 'Backspace' && step > 0) {
      e.preventDefault(); step--; renderQ();
    }
  });
})();
